// src/components/help/HelpSlideOver.jsx
// Right-side drawer that shows a help article without leaving the current page.

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useHelpPanel } from '@/contexts/HelpContext';
import { HelpMarkdownRenderer } from './HelpMarkdownRenderer';

export function HelpSlideOver() {
  const { isOpen, currentArticle, close } = useHelpPanel();

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, close]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black transition-opacity duration-300 z-40',
          isOpen ? 'bg-opacity-50' : 'bg-opacity-0 pointer-events-none'
        )}
        onClick={close}
      />

      {/* Panel */}
      <div
        className={cn(
          'fixed inset-y-0 right-0 z-50 w-full sm:w-96 bg-white shadow-xl flex flex-col',
          'transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 truncate pr-2">
            {currentArticle?.title || 'Help'}
          </h2>
          <button
            onClick={close}
            className="p-1.5 hover:bg-gray-100 rounded-lg flex-shrink-0"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {currentArticle ? (
            <>
              {/* Video player */}
              {currentArticle.videoUrl && (
                <div className="mb-4 rounded-lg overflow-hidden">
                  <video
                    src={currentArticle.videoUrl}
                    controls
                    preload="metadata"
                    className="w-full"
                  />
                </div>
              )}

              {/* Markdown body */}
              <HelpMarkdownRenderer content={currentArticle.content} />
            </>
          ) : (
            <p className="text-gray-500 text-sm">No article selected.</p>
          )}
        </div>

        {/* Footer */}
        {currentArticle && (
          <div className="px-4 py-3 border-t border-gray-200 flex-shrink-0">
            <Link
              to={`/help/${currentArticle.category}/${currentArticle.slug}`}
              onClick={close}
              className="flex items-center gap-2 text-sm text-primary hover:text-primary/80"
            >
              <ExternalLink className="h-4 w-4" />
              Open in Help Center
            </Link>
          </div>
        )}
      </div>
    </>
  );
}
